import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, Star, User, LogOut, MessageSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import Navigation from '../components/Navigation';
import BookingWizard from '../components/BookingWizard';
import { getSelectedService } from '../lib/catalog';

const ClientDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(search).get('tab');
    return ['booking', 'reviews', 'loyalty'].includes(tab) ? tab : getSelectedService() ? 'booking' : 'appointments';
  });
  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (['booking', 'reviews', 'loyalty'].includes(tab)) setActiveTab(tab);
  }, [search]);
  
  // États locaux au lieu du hook useCache pour éviter les erreurs d'ordre des hooks
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [loyaltyCard, setLoyaltyCard] = useState(null);
  const [loadingLoyalty, setLoadingLoyalty] = useState(true);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  useEffect(() => () => { if (qrImageUrl) URL.revokeObjectURL(qrImageUrl); }, [qrImageUrl]);

  // Fonction pour charger les données
  const loadDashboardData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      const data = await apiService.getDashboardData('client');
      // console.log('Dashboard data received:', data);
      // console.log('Available slots:', data?.slots);
      setDashboardData(data);
      setAppointments(data?.appointments || []);
      setAvailableSlots(data?.slots || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fonction pour charger la carte de fidélité
  const loadLoyaltyCard = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingLoyalty(true);
      const card = await apiService.getLoyaltyCard();
      setLoyaltyCard(card);
      
      // Load QR image with authentication
      await loadQrImage();
    } catch (error) {
      console.error('Error loading loyalty card:', error);
    } finally {
      setLoadingLoyalty(false);
    }
  }, [isAuthenticated]);

  // Fonction pour charger l'image QR avec authentification
  const loadQrImage = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const blob = await apiService.getLoyaltyQRImage();
      setQrImageUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.error('Error loading QR image:', error);
    }
  }, [isAuthenticated]);

  // Effet pour charger les données au montage
  useEffect(() => {
    loadDashboardData();
    loadLoyaltyCard();
  }, [loadDashboardData, loadLoyaltyCard]);

  // Redirect if not authenticated or is admin - APRÈS tous les hooks
  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace />;
  }

  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }

  const fetchData = async () => {
    try {
      await loadDashboardData();
      toast({
        title: "Succès",
        description: "Données actualisées",
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive"
      });
    }
  };

  const goToBookingDetails = (slotId) => {
    navigate(`/reserver/${slotId}`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await apiService.createReview(reviewForm);
      
      toast({
        title: "Succès",
        description: "Votre avis a été soumis et sera examiné par l'équipe !"
      });
      
      setReviewForm({ rating: 5, comment: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Erreur",
        description: "Impossible de soumettre votre avis",
        variant: "destructive"
      });
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous de votre historique ?')) {
      return;
    }
    
    try {
      await apiService.deleteAppointment(appointmentId);
      setAppointments(appointments.filter(app => app.id !== appointmentId));
      toast({
        title: "Succès",
        description: "Rendez-vous supprimé de votre historique"
      });
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        title: "Erreur", 
        description: error.response?.data?.detail || "Impossible de supprimer le rendez-vous",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', variant: 'secondary' },
      confirmed: { label: 'Confirmé', variant: 'default' },
      cancelled: { label: 'Annulé', variant: 'destructive' },
      completed: { label: 'Terminé', variant: 'success' }
    };
    
    const statusInfo = statusMap[status] || { label: status, variant: 'secondary' };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  // Fonction pour parser le lieu depuis les notes
  const parseLieuFromNotes = (notes) => {
    if (!notes) return null;
    
    const lieuMatch = notes.match(/📍 Lieu: (.+?)(?:\n|$)/);
    if (lieuMatch) {
      const lieu = lieuMatch[1].trim();
      // Convertir les valeurs techniques en affichage user-friendly
      switch(lieu) {
        case 'salon': return 'Chez moi';
        case 'domicile': return 'Chez vous';
        case 'evenement': return 'Autre';
        default: return lieu;
      }
    }
    return null;
  };

  // Fonction pour parser l'Instagram depuis les notes
  const parseInstagramFromNotes = (notes) => {
    if (!notes) return null;
    const instagramMatch = notes.match(/📱 Instagram: (.+?)(?:\n|$)/);
    return instagramMatch ? instagramMatch[1].trim() : null;
  };

  // Fonction pour parser le nombre de personnes depuis les notes
  const parsePersonnesFromNotes = (notes) => {
    if (!notes) return null;
    const personnesMatch = notes.match(/👥 Nombre de personnes: (.+?)(?:\n|$)/);
    return personnesMatch ? personnesMatch[1].trim() : null;
  };

  // Fonction pour parser les informations supplémentaires depuis les notes
  const parseInformationsSupplementairesFromNotes = (notes) => {
    if (!notes) return null;
    const infoMatch = notes.match(/ℹ️ Informations supplémentaires:\s*\n(.+?)(?=\n\n|$)/s);
    return infoMatch ? infoMatch[1].trim() : null;
  };

  const getStatusBadgeEnhanced = (status) => {
    const statusConfigs = {
      pending: { 
        label: 'En attente', 
        bgColor: 'bg-yellow-50', 
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200',
        icon: '⏳'
      },
      confirmed: { 
        label: 'Confirmé', 
        bgColor: 'bg-green-50', 
        textColor: 'text-green-800',
        borderColor: 'border-green-200',
        icon: '✅'
      },
      cancelled: { 
        label: 'Annulé', 
        bgColor: 'bg-red-50', 
        textColor: 'text-red-800',
        borderColor: 'border-red-200',
        icon: '❌'
      },
      completed: { 
        label: 'Terminé', 
        bgColor: 'bg-blue-50', 
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200',
        icon: '🎉'
      }
    };
    
    const config = statusConfigs[status] || statusConfigs.pending;
    return (
      <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        <span className="mr-1.5 text-base">{config.icon}</span>
        {config.label}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="korean-client min-h-screen bg-white">
      <Navigation />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        {/* Header épuré */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-6">
          <div className="w-full sm:w-auto">
            <div className="border-l-4 border-amber-500 pl-6">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-2 text-gray-900">
                Bonjour, {user?.first_name}
              </h1>
              <p className="text-lg text-gray-600">
                Gérez vos rendez-vous et laissez des avis
              </p>
              <div className="mt-3 flex items-center text-sm text-gray-500">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Connecté en tant que client
              </div>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <Button 
              onClick={logout} 
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto min-w-[160px] border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              <LogOut className="mr-2 h-5 w-5" />
              Déconnexion
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-gray-50 border border-gray-200 rounded-lg">
            <TabsTrigger 
              value="appointments" 
              className="text-xs sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md font-medium data-[state=active]:bg-white data-[state=active]:text-amber-600 data-[state=active]:shadow-sm text-gray-600"
            >
              <span className="hidden sm:inline">Mes Rendez-vous</span>
              <span className="sm:hidden">RDV</span>
            </TabsTrigger>
            <TabsTrigger 
              value="booking" 
              className="text-xs sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md font-medium data-[state=active]:bg-white data-[state=active]:text-amber-600 data-[state=active]:shadow-sm text-gray-600"
            >
              <span className="hidden sm:inline">Réserver</span>
              <span className="sm:hidden">Réserver</span>
            </TabsTrigger>
            <TabsTrigger 
              value="reviews" 
              className="text-xs sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md font-medium data-[state=active]:bg-white data-[state=active]:text-amber-600 data-[state=active]:shadow-sm text-gray-600"
            >
              <span className="hidden sm:inline">Laisser un Avis</span>
              <span className="sm:hidden">Avis</span>
            </TabsTrigger>
            <TabsTrigger 
              value="loyalty" 
              className="text-xs sm:text-sm py-2 sm:py-3 px-1 sm:px-3 rounded-md font-medium data-[state=active]:bg-white data-[state=active]:text-amber-600 data-[state=active]:shadow-sm text-gray-600"
            >
              <span className="hidden sm:inline">Ma Carte</span>
              <span className="sm:hidden">Carte</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="space-y-4">
            <Card className="bg-white border border-gray-200 shadow-sm rounded-lg overflow-hidden">
              <CardHeader className="bg-amber-50 border-b border-amber-100">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="mr-3 h-6 w-6 text-amber-600" />
                    <span className="text-xl font-semibold text-gray-900">Mes Rendez-vous</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchData}
                    className="bg-white hover:bg-gray-50 text-gray-700 border-gray-300"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Actualiser
                  </Button>
                </CardTitle>
                <CardDescription className="text-gray-600 text-base">
                  Consultez vos rendez-vous passés et à venir
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {appointments.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="mx-auto w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mb-6 shadow-lg">
                      <Calendar className="w-10 h-10 text-amber-600" />
                    </div>
                    <p className="text-stone-600 text-xl font-semibold mb-3">Aucun rendez-vous</p>
                    <p className="text-gray-500 text-base mb-8 max-w-md mx-auto">Vous n'avez pas encore de rendez-vous programmés. Réservez dès maintenant !</p>
                    <Button 
                      onClick={() => document.querySelector('[value="booking"]').click()} 
                      className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      <Calendar className="w-5 h-5 mr-3" />
                      Réserver maintenant
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {appointments.filter((appointment) => {
                      // Masquer les RDV passés de plus de 24h
                      if (appointment.status === 'completed' || appointment.status === 'cancelled') {
                        const appointmentDate = new Date(
                          (appointment.slot_info && appointment.slot_info.date) || appointment.date || new Date()
                        );
                        const now = new Date();
                        const hoursDiff = (now - appointmentDate) / (1000 * 60 * 60);
                        
                        // Masquer si passé depuis plus de 24h
                        if (hoursDiff > 24) {
                          return false;
                        }
                      }
                      return true;
                    }).map((appointment) => {
                      const isUpcoming = appointment.status === 'confirmed' || appointment.status === 'pending';
                      const isCompleted = appointment.status === 'completed';
                      const isCancelled = appointment.status === 'cancelled';
                      
                      // Vérifier si le RDV est passé (pour le bouton supprimer)
                      const appointmentDate = new Date(
                        (appointment.slot_info && appointment.slot_info.date) || appointment.date || new Date()
                      );
                      const now = new Date();
                      const isPastAppointment = appointmentDate < now;
                      
                      // Parser les informations depuis les notes
                      const lieu = parseLieuFromNotes(appointment.notes);
                      const instagram = parseInstagramFromNotes(appointment.notes);
                      const personnes = parsePersonnesFromNotes(appointment.notes);
                      const informationsSupplementaires = parseInformationsSupplementairesFromNotes(appointment.notes);
                      
                      return (
                        <div key={appointment.id} className={`border rounded-lg p-6 transition-all duration-200 hover:shadow-md ${
                          isUpcoming ? 'border-amber-200 bg-amber-50/30' : 
                          isCompleted ? 'border-green-200 bg-green-50/30' :
                          isCancelled ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-white'
                        }`}>
                          <div className="flex flex-col lg:flex-row gap-6">
                            {/* Service Info */}
                            <div className="flex-1 space-y-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                    {appointment.service_name || 'Service non spécifié'}
                                  </h3>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    <div className="flex items-center bg-white rounded-md p-3 border border-gray-100">
                                      <Calendar className="w-5 h-5 mr-3 text-amber-500 flex-shrink-0" />
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Date</div>
                                        <div className="font-medium text-gray-900">
                                          {(appointment.slot_info && appointment.slot_info.date) || appointment.date ? 
                                            formatDate((appointment.slot_info && appointment.slot_info.date) || appointment.date) : 
                                            'Date non spécifiée'}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center bg-white rounded-md p-3 border border-gray-100">
                                      <Clock className="w-5 h-5 mr-3 text-amber-500 flex-shrink-0" />
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Heure</div>
                                        <div className="font-medium text-gray-900">
                                          {(appointment.slot_info && appointment.slot_info.start_time) || appointment.start_time ? 
                                            `${formatTime((appointment.slot_info && appointment.slot_info.start_time) || appointment.start_time)}` : 
                                            'Heure non spécifiée'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Status et Prix */}
                                <div className="flex flex-col items-end gap-3 ml-4">
                                  {getStatusBadgeEnhanced(appointment.status)}
                                  <div className="bg-amber-500 text-white px-4 py-2 rounded-md font-semibold text-lg">
                                    {appointment.service_price || 0}€
                                  </div>
                                </div>
                              </div>

                              {/* Informations détaillées */}
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {lieu && (
                                  <div className="bg-white rounded-md p-3 border border-gray-100">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-sm">📍</span>
                                      </div>
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Lieu</div>
                                        <div className="font-medium text-gray-900">{lieu}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {personnes && (
                                  <div className="bg-white rounded-md p-3 border border-gray-100">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-sm">👥</span>
                                      </div>
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Personnes</div>
                                        <div className="font-medium text-gray-900">{personnes}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {instagram && (
                                  <div className="bg-white rounded-md p-3 border border-gray-100">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-sm">📱</span>
                                      </div>
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Instagram</div>
                                        <div className="font-medium text-gray-900">{instagram}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                {informationsSupplementaires && (
                                  <div className="bg-white rounded-md p-3 border border-gray-100">
                                    <div className="flex items-start">
                                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                                        <span className="text-sm">💬</span>
                                      </div>
                                      <div>
                                        <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">Notes supplémentaires</div>
                                        <div className="font-medium text-gray-900 text-sm whitespace-pre-line">{informationsSupplementaires}</div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Actions pour les RDV à venir */}
                              {isUpcoming && (
                                <div className="flex justify-center pt-2">
                                  <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-800 border border-blue-200">
                                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    {appointment.status === 'pending' ? 'En attente de confirmation' : 'Confirmé - À venir'}
                                  </div>
                                </div>
                              )}
                              
                              {/* Bouton Supprimer pour les RDV passés */}
                              {isPastAppointment && (isCompleted || isCancelled) && (
                                <div className="flex justify-center pt-4">
                                  <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteAppointment(appointment.id)}
                                    className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all duration-300"
                                  >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Supprimer
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="booking" className="space-y-4">
            <Card className="bg-white/95 backdrop-blur-sm border-2 border-amber-100 shadow-xl rounded-2xl overflow-hidden">
              <CardContent className="p-6">
                {availableSlots.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="mx-auto w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mb-6 shadow-lg">
                      <Clock className="w-10 h-10 text-amber-600" />
                    </div>
                    <p className="text-gray-800 text-xl font-semibold mb-3">Aucun créneau disponible</p>
                    <p className="text-gray-600 text-base mb-8 max-w-md mx-auto">Les créneaux sont mis à jour régulièrement. Revenez bientôt !</p>
                    <Button 
                      variant="outline" 
                      onClick={fetchData}
                      className="bg-white hover:bg-amber-50 border-amber-200 hover:border-amber-400 transition-all duration-300 px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl"
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Vérifier les disponibilités
                    </Button>
                  </div>
                ) : (
                  <BookingWizard 
                    availableSlots={availableSlots} 
                    onBookingComplete={fetchData}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <Card className="bg-white/70/80 backdrop-blur-sm border-2 border-amber-100 shadow-xl rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                <CardTitle className="flex items-center">
                  <Star className="mr-3 h-6 w-6" />
                  <span className="text-xl font-bold">Laisser un Avis</span>
                </CardTitle>
                <CardDescription className="text-amber-100 text-base">
                  Partagez votre expérience avec nous
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={submitReview} className="space-y-8">
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold text-gray-800 flex items-center">
                      <span className="mr-2">⭐</span>
                      Votre note
                    </Label>
                    <Select 
                      value={reviewForm.rating.toString()} 
                      onValueChange={(value) => setReviewForm({...reviewForm, rating: parseInt(value)})}
                    >
                      <SelectTrigger className="w-full h-14 text-base bg-white/70/90 border-2 border-amber-200 rounded-xl hover:border-amber-300 transition-all duration-300 shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white/70/95 backdrop-blur-sm border-2 border-amber-200 rounded-xl shadow-xl">
                        <SelectItem value="5" className="text-base py-3 hover:bg-amber-50">
                          <div className="flex items-center">
                            <span className="mr-3 text-xl">⭐⭐⭐⭐⭐</span>
                            <span className="font-semibold">Excellent (5/5)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="4" className="text-base py-3 hover:bg-amber-50">
                          <div className="flex items-center">
                            <span className="mr-3 text-xl">⭐⭐⭐⭐</span>
                            <span className="font-semibold">Très bien (4/5)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="3" className="text-base py-3 hover:bg-amber-50">
                          <div className="flex items-center">
                            <span className="mr-3 text-xl">⭐⭐⭐</span>
                            <span className="font-semibold">Bien (3/5)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="2" className="text-base py-3 hover:bg-amber-50">
                          <div className="flex items-center">
                            <span className="mr-3 text-xl">⭐⭐</span>
                            <span className="font-semibold">Moyen (2/5)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="1" className="text-base py-3 hover:bg-amber-50">
                          <div className="flex items-center">
                            <span className="mr-3 text-xl">⭐</span>
                            <span className="font-semibold">Décevant (1/5)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-4">
                    <Label htmlFor="comment" className="text-lg font-semibold text-gray-800 flex items-center">
                      <span className="mr-2">💬</span>
                      Votre commentaire
                    </Label>
                    <Textarea
                      id="comment"
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                      placeholder="Partagez votre expérience avec nous... Qu'avez-vous pensé du service ? Y a-t-il quelque chose que vous aimeriez améliorer ?"
                      className="min-h-[120px] text-base bg-white/70/90 border-2 border-amber-200 rounded-xl hover:border-amber-300 focus:border-pink-400 transition-all duration-300 shadow-sm resize-none"
                      required
                    />
                    <p className="text-sm text-gray-500 italic">
                      💡 Votre avis nous aide à améliorer nos services et aide d'autres clients à faire leur choix
                    </p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full py-4 text-lg font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <Star className="w-5 h-5 mr-3" />
                    Soumettre mon avis
                  </Button>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200">
                    <p className="text-sm text-blue-700 font-medium flex items-center">
                      <span className="mr-2">ℹ️</span>
                      Votre avis sera examiné par notre équipe avant publication pour maintenir la qualité de nos services
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="loyalty" className="korean-loyalty">
            {loadingLoyalty ? (
              <Card className="bg-white/70/80 backdrop-blur-sm border-2 border-amber-100 shadow-xl rounded-2xl overflow-hidden">
                <CardContent className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                  <p className="mt-4 text-stone-600">Chargement de votre carte de fidélité...</p>
                </CardContent>
              </Card>
            ) : loyaltyCard ? (
              <div className="space-y-6">
                {/* Carte de fidélité principale */}
                <Card className="bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600 text-white shadow-2xl rounded-3xl overflow-hidden border-4 border-amber-300">
                  <CardContent className="p-8">
                    <div className="text-center mb-8">
                      <h3 className="text-2xl sm:text-3xl font-bold mb-2 flex items-center justify-center">
                        Votre carte by Ali
                      </h3>
                      <p className="text-amber-100 text-lg">
                        Code: <span className="font-mono font-bold text-2xl">{loyaltyCard.qr_code}</span>
                      </p>
                    </div>
                    
                    {/* Progression des tampons */}
                    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-semibold">Progression</span>
                        <span className="text-xl font-bold">{loyaltyCard.stamps_count}/6</span>
                      </div>
                      
                      {/* Barre de progression visuelle */}
                      <div className="grid grid-cols-6 gap-3 mb-4">
                        {[...Array(6)].map((_, index) => (
                          <div
                            key={index}
                            className={`aspect-square rounded-xl border-3 flex items-center justify-center text-2xl transition-all duration-300 ${
                              index < loyaltyCard.stamps_count
                                ? 'bg-white text-amber-600 border-white shadow-lg scale-110'
                                : 'bg-gray-800/50 border-white/50 text-white/70'
                            }`}
                          >
                            {index < loyaltyCard.stamps_count ? '✦' : String(index + 1).padStart(2, '0')}
                          </div>
                        ))}
                      </div>
                      
                      {/* Message de progression */}
                      {loyaltyCard.stamps_count < 6 ? (
                        <p className="text-center text-white/90 font-medium">
                          Plus que {6 - loyaltyCard.stamps_count} tampons pour débloquer votre réduction de 10 € !
                        </p>
                      ) : (
                        <p className="text-center text-white font-bold text-lg animate-pulse">
                          🎉 Félicitations ! Réduction de 10 € débloquée !
                        </p>
                      )}
                    </div>
                    
                    {/* QR Code */}
                    <div className="text-center">
                      <h4 className="text-lg font-semibold mb-4">Votre QR Code Personnel</h4>
                      <div className="bg-white p-4 rounded-2xl inline-block shadow-lg">
                        {qrImageUrl ? (
                          <img 
                            src={qrImageUrl}
                            alt="QR Code Fidélité" 
                            className="w-48 h-48 mx-auto"
                            style={{ imageRendering: 'pixelated' }}
                          />
                        ) : (
                          <div className="w-32 h-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center">
                            <div className="text-gray-500 text-sm text-center">
                              QR indisponible. Utilisez les 4 chiffres.
                            </div>
                          </div>
                        )}
                        <p className="text-neutral-900 text-sm mt-2 font-mono font-bold">
                          {loyaltyCard.qr_code}
                        </p>
                      </div>
                      <p className="text-amber-100 text-sm mt-4">
                        Présentez ce QR code à Ali après votre soin pour recevoir votre tampon.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Avantages disponibles */}
                {loyaltyCard.discount_available && (
                  <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-xl rounded-2xl overflow-hidden border-2 border-green-300">
                    <CardContent className="p-6 text-center">
                      <div className="animate-bounce mb-4">
                        <span className="text-6xl">🎉</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-2">Réduction Disponible !</h3>
                      <p className="text-xl font-semibold mb-4">10 € sur votre prochain service</p>
                      <p className="text-green-100">
                        Mentionnez cette réduction lors de votre prochaine réservation pour en bénéficier !
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Historique */}
                <Card className="bg-white/70/80 backdrop-blur-sm border-2 border-amber-100 shadow-xl rounded-2xl overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                    <CardTitle className="text-xl font-bold flex items-center">
                      📊 Statistiques de Fidélité
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-amber-600 mb-2">
                          {loyaltyCard.total_stamps_earned}
                        </div>
                        <p className="text-stone-600 font-medium">Tampons totaux</p>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-green-500 mb-2">
                          {Math.floor(loyaltyCard.total_stamps_earned / 6)}
                        </div>
                        <p className="text-stone-600 font-medium">Réductions obtenues</p>
                      </div>
                    </div>
                    
                    {loyaltyCard.stamps && loyaltyCard.stamps.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-4">Derniers tampons</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {loyaltyCard.stamps.slice(-5).reverse().map((stamp, index) => (
                            <div key={stamp.id} className="flex justify-between items-center text-sm bg-gray-50 p-3 rounded-lg">
                              <span className="font-medium">Tampon #{loyaltyCard.stamps.length - index}</span>
                              <span className="text-gray-500">
                                {new Date(stamp.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Instructions */}
                <Card className="bg-blue-50 border-2 border-blue-200 shadow-lg rounded-2xl overflow-hidden">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-blue-800 mb-4 flex items-center">
                      ℹ️ Comment ça marche ?
                    </h3>
                    <div className="space-y-3 text-blue-700">
                      <div className="flex items-start">
                        <span className="font-bold mr-2">1.</span>
                        <span>Présentez votre code QR (affiché ci-dessus) à l'admin après votre service</span>
                      </div>
                      <div className="flex items-start">
                        <span className="font-bold mr-2">2.</span>
                        <span>L'admin saisira votre code pour valider un tampon</span>
                      </div>
                      <div className="flex items-start">
                        <span className="font-bold mr-2">3.</span>
                        <span>Après 6 tampons, vous débloquez une réduction de 10 €</span>
                      </div>
                      <div className="flex items-start">
                        <span className="font-bold mr-2">4.</span>
                        <span>Votre carte se remet à zéro après utilisation de la réduction</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="bg-white/70/80 backdrop-blur-sm border-2 border-amber-100 shadow-xl rounded-2xl overflow-hidden">
                <CardContent className="p-8 text-center">
                  <div className="text-6xl mb-4">🎁</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Aucune carte de fidélité</h3>
                  <p className="text-stone-600">Effectuez votre première réservation pour obtenir votre carte de fidélité !</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ClientDashboard;
